import React, { useMemo } from "react";

const DonutChart = ({
  data = [],
  centerLabel = "Total",
  centerValue = null,
  size = 200,
  strokeWidth = 28,
}) => {
  const radius = (size - strokeWidth) / 2;
  const viewBox = `0 0 ${size} ${size}`;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  const { totalValue, segments } = useMemo(() => {
    const total = data.reduce((acc, item) => acc + item.value, 0);

    // If no data, return empty state to avoid division by zero
    if (total === 0) return { totalValue: 0, segments: [] };

    let currentOffset = 0;

    const computedSegments = data.map((item) => {
      const segmentPercentage = item.value / total;
      const segmentLength = segmentPercentage * circumference;

      // The offset starts at the current accumulated position
      const offset = currentOffset;

      // Update the offset for the next segment
      currentOffset -= segmentLength;

      return {
        ...item,
        length: segmentLength,
        offset: offset,
      };
    });

    return { totalValue: total, segments: computedSegments };
  }, [data, circumference]);

  const displayValue = centerValue !== null ? centerValue : totalValue;

  return (
    <div
      className="donut-wrapper"
      style={{ width: size, height: size, position: "relative" }}
    >
      <svg
        width={size}
        height={size}
        viewBox={viewBox}
        style={{ transform: "rotate(-90deg)" }} // Start at the top (12 o'clock)
      >
        {segments.map((segment, index) => (
          <circle
            key={index}
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke={segment.color}
            strokeWidth={strokeWidth}
            /* strokeDasharray: [length of dash] [length of gap]
               We set the gap to the full circumference to ensure only one segment shows per circle
            */
            strokeDasharray={`${segment.length} ${circumference}`}
            strokeDashoffset={segment.offset}
            strokeLinecap="butt"
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
        ))}
      </svg>

      <div
        className="donut-center-content"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <span
          className="tiny-label-bold"
          style={{ color: "var(--text-secondary)", marginBottom: "4px" }}
        >
          {centerLabel}
        </span>
        <h3
          className="margin-reset"
          style={{ fontSize: "1.4rem", fontWeight: "700" }}
        >
          ${displayValue.toLocaleString()}
        </h3>
      </div>
    </div>
  );
};

export default DonutChart;
